import type { Assistant } from "@inrev/assistant";
import type { AssistantConversationId } from "@inrev/common";
import { Option } from "@inrev/core";
import { useFetchAssistantCitations } from "@portal/api/assistant/admin";
import { CitationMarker } from "@portal/components/patterns/citation-marker";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import Markdown, { type Components, defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useOpenCitation } from "./AdminAssistantCitationPreview";
import { tableComponents } from "./AdminAssistantMarkdownTable";
import {
	citationEvidenceId,
	citationEvidenceIds,
	citationKey,
	citationKeys,
	citationKindLabel,
	documentCitation,
	isCitationHref,
	parseCitation,
	withCitationLinks,
} from "./assistantCitations";
import { AdminAssistantChart } from "./chart/AdminAssistantChart";
import {
	chartBlockSource,
	openChartBlockStarts,
	withClosedChartFences,
} from "./chart/assistantChartFence";

type CitationContextValue = {
	keys: string[];
	labels: ReadonlyMap<string, Assistant.Dtos.Citation.Resolve.Item>;
	/** Evidence ids whose record no longer exists. */
	missing: ReadonlySet<string>;
};

const NO_LABELS: ReadonlyMap<string, Assistant.Dtos.Citation.Resolve.Item> = new Map();
const NO_MISSING: ReadonlySet<string> = new Set();
const NO_EVIDENCE_IDS: Assistant.Tools.EvidenceId[] = [];

const CitationContext = createContext<CitationContextValue>({
	keys: [],
	labels: NO_LABELS,
	missing: NO_MISSING,
});

const NO_OPEN_CHARTS: ReadonlySet<number> = new Set();

type ChartContextValue = {
	/** The whole answer is still streaming. */
	streaming: boolean;
	/** Where the chart blocks still streaming in start. A closed block is finished even mid-answer. */
	open: ReadonlySet<number>;
	conversationId: AssistantConversationId | undefined;
};

/**
 * Lets a chart block tell incomplete JSON from an invalid finished block, and find a drawn
 * chart's image, which is stored under its conversation.
 */
const ChartContext = createContext<ChartContextValue>({
	streaming: false,
	open: NO_OPEN_CHARTS,
	conversationId: undefined,
});

const ChartBlock = ({ source, start }: { source: string; start: number | undefined }) => {
	const { streaming, open, conversationId } = useContext(ChartContext);
	return (
		<AdminAssistantChart
			source={source}
			open={start !== undefined && open.has(start)}
			streaming={streaming}
			conversationId={conversationId}
		/>
	);
};

const NO_SOURCES: readonly string[] = [];

/** The web pages the answer's tools returned, as `sourceLinkKey`s. */
const SourceContext = createContext<ReadonlySet<string>>(new Set());

/**
 * Spellings of one page match: the host's case, a leading `www.`, and a trailing slash differ
 * harmlessly when the model copies a URL. The query string and the rest of the path must match exactly, since
 * that is where data would ride. Only https is ever a link, so a `mailto:` never is.
 */
const sourceLinkKey = (url: string): string | undefined => {
	try {
		const parsed = new URL(url);
		// `https://<data>@host/page` would otherwise match `https://host/page`: the userinfo is
		// not in `host`, and the click would carry it off.
		if (parsed.protocol !== "https:" || parsed.username !== "" || parsed.password !== "") {
			return undefined;
		}
		const host = parsed.host.replace(/^www\./, "");
		const path = parsed.pathname.length > 1 ? parsed.pathname.replace(/\/$/, "") : parsed.pathname;
		return `${parsed.protocol}//${host}${path}${parsed.search}${parsed.hash}`;
	} catch {
		return undefined;
	}
};

/**
 * Only a URL the answer's own web tools returned is a link. Anything else -- including a link a
 * poisoned page talked the model into writing, with account data in its query string -- shows
 * as text with its URL spelled out, so nothing leaves on a click.
 */
const OutsideLink = ({ children, href }: { children: ReactNode; href: string | undefined }) => {
	const sources = useContext(SourceContext);
	const key = href === undefined ? undefined : sourceLinkKey(href);
	if (key !== undefined && sources.has(key)) {
		return (
			<a className="text-link underline" href={href} rel="noreferrer" target="_blank">
				{children}
			</a>
		);
	}
	return (
		<span data-testid="assistant-unlinked-url">
			{children}
			{href !== undefined && children !== href && <span className="text-tertiary"> ({href})</span>}
		</span>
	);
};

const Citation = ({ href, title }: { href: string; title: string | undefined }) => {
	const { keys, labels, missing } = useContext(CitationContext);
	const openCitation = useOpenCitation();
	const citation = parseCitation(href, title);
	const document = citation === undefined ? undefined : documentCitation(citation);
	const evidenceId = citation === undefined ? undefined : citationEvidenceId(citation);
	const label = evidenceId === undefined ? undefined : labels.get(evidenceId);
	const unavailable = evidenceId !== undefined && missing.has(evidenceId);
	// A document read's own title carries the page, which the database label cannot know.
	const heading = citation?.kind === "file" ? (title ?? label?.title) : (label?.title ?? title);

	return (
		<CitationMarker
			detail={label?.detail ?? undefined}
			heading={heading}
			kind={citation === undefined ? "Source" : citationKindLabel(citation)}
			number={keys.indexOf(citationKey(href)) + 1}
			onOpen={
				openCitation === undefined || document === undefined
					? undefined
					: () => openCitation(document)
			}
			testId="assistant-citation"
			unavailable={unavailable}
		/>
	);
};

const urlTransform = (url: string) => (isCitationHref(url) ? url : defaultUrlTransform(url));

const components: Components = {
	...tableComponents,
	a: ({ children, href, title }) =>
		isCitationHref(href) ? (
			<Citation href={href} title={title} />
		) : (
			<OutsideLink href={href}>{children}</OutsideLink>
		),
	blockquote: ({ children }) => (
		<blockquote className="border-l-2 border-bc-default pl-2 text-secondary">{children}</blockquote>
	),
	code: ({ children, className }) =>
		className === undefined ? (
			<code className="rounded bg-surface-light px-1 py-[1px] font-mono text-[12.5px]">
				{children}
			</code>
		) : (
			<code className="font-mono text-[12.5px]">{children}</code>
		),
	em: ({ children }) => <em className="italic">{children}</em>,
	h1: ({ children }) => (
		<h1 className="text-[16px] font-semibold leading-[22px] text-primary">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="text-[15px] font-semibold leading-[21px] text-primary">{children}</h2>
	),
	h3: ({ children }) => <h3 className="font-semibold text-primary">{children}</h3>,
	h4: ({ children }) => <h4 className="font-semibold text-primary">{children}</h4>,
	hr: () => <hr className="border-bc-subtle" />,
	li: ({ children }) => <li className="[&>ol]:mt-0.5 [&>ul]:mt-0.5">{children}</li>,
	ol: ({ children }) => (
		<ol className="flex list-decimal flex-col gap-0.5 pl-[18px]">{children}</ol>
	),
	p: ({ children }) => <p className="text-pretty">{children}</p>,
	pre: ({ children, node }) => {
		const chartSource = chartBlockSource(node);
		return chartSource === undefined ? (
			<pre className="overflow-x-auto rounded bg-surface-light p-2">{children}</pre>
		) : (
			<ChartBlock source={chartSource} start={node?.position?.start.offset} />
		);
	},
	strong: ({ children }) => <strong className="font-semibold text-primary">{children}</strong>,
	ul: ({ children }) => <ul className="flex list-disc flex-col gap-0.5 pl-[18px]">{children}</ul>,
	img: () => null,
};

/**
 * Renders assistant text as markdown. The agent replies in markdown, and its lists and emphasis
 * are unreadable as raw text in a panel this narrow. Raw HTML is not enabled, so model output
 * cannot inject markup.
 *
 * A `cite:` link is a record citation, not a link: it renders as a numbered superscript whose
 * tooltip names the kind of record and what it is, looked up from the database on the
 * conversation's account, falling back to the agent's own title (a document read keeps its
 * title, which carries the page). Inside a citation preview provider, a PDF citation is a button
 * that opens the file at its first cited page. A citation whose record no longer exists is muted,
 * says so, and opens nothing.
 *
 * A ```chart fence is a chart request, not code: it renders the chart its block names (see
 * `Assistant.Charts`), or an image the agent drew.
 *
 * An outside link is a link only when `sources` holds its URL; see `OutsideLink`.
 *
 * Prose is set in `text-secondary` and emphasis, headings, and metric values in `text-primary`,
 * so the figures an answer turns on carry the weight rather than the sentences around them.
 */
export const AdminAssistantMarkdown = ({
	conversationId,
	sources = NO_SOURCES,
	streaming = false,
	text,
}: {
	/** The conversation whose account cited records are labeled from; without it, the agent's titles show. */
	conversationId?: AssistantConversationId;
	/** The web pages the answer's tools returned; the only outside links that render as links. */
	sources?: readonly string[];
	/** Defers the label lookup until the answer is whole, so each new citation does not refetch. */
	streaming?: boolean;
	text: string;
}) => {
	const sourceKeys = useMemo(
		() => new Set(sources.map(sourceLinkKey).filter(Option.isSome)),
		[sources],
	);
	const linkedText = useMemo(() => withCitationLinks(withClosedChartFences(text)), [text]);
	const openCharts = useMemo(
		() => (streaming ? openChartBlockStarts(linkedText) : NO_OPEN_CHARTS),
		[streaming, linkedText],
	);
	const evidenceIds = useMemo(
		() => (streaming ? NO_EVIDENCE_IDS : citationEvidenceIds(text)),
		[streaming, text],
	);
	const { citations: resolved, missing } = useFetchAssistantCitations(conversationId, evidenceIds);
	const chart = useMemo(
		() => ({ streaming, open: openCharts, conversationId }),
		[streaming, openCharts, conversationId],
	);
	const citations = useMemo(
		() => ({
			keys: citationKeys(text),
			labels: new Map(resolved.map((item) => [item.id, item])),
			missing: new Set(missing),
		}),
		[text, resolved, missing],
	);
	return (
		<CitationContext.Provider value={citations}>
			<SourceContext.Provider value={sourceKeys}>
				<ChartContext.Provider value={chart}>
					<div className="flex flex-col gap-2 break-words text-[14px] font-normal leading-[20px] text-secondary [&>ol]:-mt-1 [&>ul]:-mt-1">
						<Markdown
							components={components}
							remarkPlugins={[remarkGfm]}
							urlTransform={urlTransform}
						>
							{linkedText}
						</Markdown>
					</div>
				</ChartContext.Provider>
			</SourceContext.Provider>
		</CitationContext.Provider>
	);
};
